import { useRef, useState, useEffect } from 'react';
import className from 'classnames/bind';
import { Button, Container, Image, HeadlineAnimSplitLine } from '@components';
import { useDebug } from '@/utils/useDebugLog';
import type { GetCoverStoriesQuery } from '@graphqlTypes';
import { useGSAP } from '@gsap/react';
import { useClientMount } from '@/providers/ClientMountProvider';
import { useViewSize } from '@/providers/ViewSizeContext';
import gsap from 'gsap';
import { hasCompletedGame, getCompletedGame } from '@/components/CoverStory/Game/config/gameLocalStorage';

import styles from './CoverStoryHero.module.scss';
import { getHighlightMarkup } from '@/core/utils';
const cx = className.bind(styles);

type CoverStoryHeroProps = CoverStoryLanding['coverStoryHero'] & {
  latestGame?: GetCoverStoriesQuery['coverStories']['nodes'][0];
  resumeCtaLabel?: string;
  solvedCtaLabel?: string;
  lostCtaLabel?: string;
};

export default function CoverStoryHero({ headline, subhead, ctaLabel, resumeCtaLabel, solvedCtaLabel, lostCtaLabel, image, latestGame }: CoverStoryHeroProps) {
  const { log } = useDebug({ name: 'CoverStoryHero', bgColor: 'rebeccapurple', textColor: 'white', enabled: false });

  log({ headline, subhead, ctaLabel, resumeCtaLabel, solvedCtaLabel, lostCtaLabel, latestGame });

  // -------------------- CMS data setup

  const Copy_Hero = {
    headline: headline || 'Missing headline',
    subhead: subhead || 'Missing subhead',
    ctaLabel: ctaLabel || 'Missing play game',
    resumeCtaLabel: resumeCtaLabel || 'Missing resume game',
    solvedCtaLabel: solvedCtaLabel || 'Missing played',
    lostCtaLabel: lostCtaLabel || 'Missing you lost',
  };

  const headlineMarkup = getHighlightMarkup(Copy_Hero.headline).replace(/<br\s*\/?>/gi, '');
  // Note: replaced <br> tags with spaces to avoid breaking the animation

  // Get latest game info
  const gameNumber = latestGame?.gameData.gameNumber || null;
  const gameTitle = latestGame?.title || null;
  const gameUri = latestGame?.uri || '#';

  // -------------------- Refs and State

  const rootContentRef = useRef<HTMLDivElement>(null);
  const subheadRef = useRef<HTMLParagraphElement>(null);
  const currentGameRef = useRef<HTMLDivElement>(null);
  const [animationKey, setAnimationKey] = useState(0);
  const [hasPlayedCurrentGame, setHasPlayedCurrentGame] = useState(false);
  const [didWinCurrentGame, setDidWinCurrentGame] = useState<boolean | null>(null);

  const { mounted } = useClientMount();
  const { isReady } = useViewSize();

  // -------------------- Effects

  // Current game: has the user played?
  useEffect(() => {
    if (!mounted || !gameNumber) return;

    const completedGame = getCompletedGame(String(gameNumber));
    if (completedGame) {
      setHasPlayedCurrentGame(true);
      setDidWinCurrentGame(completedGame.won);
      log('Checked game completion status', { gameNumber, played: true, won: completedGame.won });
    } else {
      setHasPlayedCurrentGame(false);
      setDidWinCurrentGame(null);
    }
  }, [mounted, gameNumber]);

  // -------------------- Animation

  useGSAP(() => {
    // Only run when mounted and ready
    if (!mounted || !isReady) return;
    if (!subheadRef.current || !currentGameRef.current) return;

    log('Animation initialized');
    initializeAnimations();
  }, [mounted, isReady, animationKey]);

  // Initialize animations on mount
  const initializeAnimations = () => {
    log('initializeAnimations');

    // Create timeline for remaining elements
    const tl = gsap.timeline().add('start', '+=0.9');

    if (rootContentRef.current) {
      tl.fromTo(rootContentRef.current, {
        y: '6rem',
        opacity: 0
      }, {
        y: '0rem',
        opacity: 1,
        duration: 0.5,
        ease: 'power3.out',
      }, 0);
    }

    const animDistance = '1rem';

    // Animate subhead
    if (subheadRef.current) {
      tl.fromTo(subheadRef.current, {
        opacity: 0,
        y: animDistance,
      }, {
        opacity: 1,
        y: 0,
        duration: 0.3,
        ease: 'power1.out',
      }, 'start');
    }

    // Animate current-game
    if (currentGameRef.current) {
      tl.fromTo(currentGameRef.current, {
        opacity: 0,
        y: animDistance,
      }, {
        opacity: 1,
        y: 0,
        duration: 0.3,
        ease: 'power1.out',
      }, '-=0.2');
    }
  };

  // Debug: Replay animation
  const handleReplayAnimation = () => {
    log('handleReplayAnimation');

    // Kill any existing animations
    gsap.killTweensOf([subheadRef.current, currentGameRef.current]);

    // Reset elements to initial state
    if (subheadRef.current) {
      gsap.set(subheadRef.current, { opacity: 0, y: 20 });
    }
    if (currentGameRef.current) {
      gsap.set(currentGameRef.current, { opacity: 0, y: 30 });
    }

    // Increment animation key to trigger re-render and replay
    setAnimationKey(prev => prev + 1);
  };

  // -------------------- Render

  return (
    <section className={cx(["cover-story-hero"])}>
      <Container className={cx("container")}>
        <div className={cx("content")} ref={rootContentRef}>
          <Image image={image} sizes="2048px" className={cx("image")} />
          <div className={cx("copy")}>

            {/* Headline */}
            <HeadlineAnimSplitLine
              key={animationKey}
              // isCenterAlign={true}
              tag="h1"
              text={headlineMarkup}
              className={cx(["headline", "title-uc-xxxxl"])}
              isRevealEnabled={true}
              isInView={true}
              // animInDelay={0.2}
              // onAnimationComplete={handleHeadlineAnimComplete}
            />

            {/* Subhead */}
            <p className={cx(["subhead", "title-lc-xs"])}
              ref={subheadRef}
              dangerouslySetInnerHTML={{ __html: Copy_Hero.subhead }}
            />

            {/* Current Game */}
            <div ref={currentGameRef} className={cx("current-game")}>
              <p className={cx(["text", "title-lc-xs"])}>
                {`#${gameNumber} - ${gameTitle}`}
              </p>
              <Button
                label={
                  !hasPlayedCurrentGame 
                    ? Copy_Hero.ctaLabel
                    : didWinCurrentGame 
                      ? Copy_Hero.solvedCtaLabel
                      : Copy_Hero.lostCtaLabel
                }
                url={gameUri}
              />
            </div>

          </div>
        </div>
      </Container>

      {/* Debug Button - Replay Animation */}
      {/* <button className={cx("debug-button")} onClick={handleReplayAnimation}>
        Replay Animation
      </button> */}

    </section>
  );

}
